import React, { useEffect, useState } from "react";
import UnicornStudio from "unicornstudio-react";
import { useIsFocused } from "@react-navigation/native";

const hasDom = typeof window !== "undefined" && typeof document !== "undefined";

export interface AuroraBackgroundProps {
  /** Path to the bundled Unicorn Studio scene JSON. */
  jsonFilePath: string;
}

/**
 * Ambient WebGL aurora background.
 *
 * Wraps `unicornstudio-react` with the performance guards the raw component lacks:
 * the render loop is capped at 30fps and paused whenever the scene isn't actually
 * visible (route blurred or browser tab backgrounded), and it is skipped entirely
 * for users who prefer reduced motion, when there is no DOM, or when the scene
 * fails to load. This stops the full-viewport fragment shader from burning GPU on
 * an idle or backgrounded page (ENG-1525).
 *
 * Callers own the positioning wrapper (size, pointerEvents, zIndex); this renders
 * only the scene — or nothing — so existing layouts are unaffected.
 */
export const AuroraBackground = ({ jsonFilePath }: AuroraBackgroundProps) => {
  const [hasError, setHasError] = useState(false);
  const isFocused = useIsFocused();
  const tabVisible = useTabVisible();
  const prefersReducedMotion = usePrefersReducedMotion();

  if (!hasDom || hasError || prefersReducedMotion) return null;

  return (
    <UnicornStudio
      jsonFilePath={jsonFilePath}
      width="100%"
      height="100%"
      scale={1}
      dpi={1}
      fps={30}
      paused={!isFocused || !tabVisible}
      lazyLoad={false}
      onError={() => setHasError(true)}
    />
  );
};

/** Tracks whether the browser tab is foregrounded; always true without a DOM. */
const useTabVisible = () => {
  const [visible, setVisible] = useState(() => (hasDom ? document.visibilityState !== "hidden" : true));
  useEffect(() => {
    if (!hasDom) return;
    const onVisibilityChange = () => setVisible(document.visibilityState !== "hidden");
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, []);
  return visible;
};

/** Tracks the OS-level "prefers reduced motion" setting. */
const usePrefersReducedMotion = () => {
  const query = () =>
    hasDom && typeof window.matchMedia === "function"
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false;
  const [reduced, setReduced] = useState(query);
  useEffect(() => {
    if (!hasDom || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const onChange = (event: MediaQueryListEvent) => setReduced(event.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return reduced;
};

export default AuroraBackground;
